import React, { useState, useRef, useEffect } from 'react';
import VisitConfirmationModal from '~/components/VisitConfirmationModal';
import VisitValidationModal from '~/components/VisitValidationModal';
import VisitNotConfirmedModal from '~/components/VisitNotConfirmedModal';
import ResponseRegisteredModal from '~/components/ResponseRegisteredModal';
import FeedbackModal from '~/components/FeedbackModal';
import ConsultantRatingModal from '~/components/ConsultantRatingModal';
import Loading from '~/components/layout/Loading';
import {
  updateCommitment,
} from '~/services/commitmentService';
import { analytics, logEvent } from '~/services/firebaseConfig';

const VisitModalController = ({ visitData, onClearVisit }) => {
  const [showVisitModal, setShowVisitModal] = useState(!!visitData);
  const [showVisitValidationModal, setShowVisitValidationModal] = useState(false);
  const [showVisitNotConfirmedModal, setShowVisitNotConfirmedModal] = useState(false);
  const [deniedCommitmentId, setDeniedCommitmentId] = useState(null);
  const [showResponseRegisteredModal, setShowResponseRegisteredModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showConsultantRatingModal, setShowConsultantRatingModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [localVisitData, setLocalVisitData] = useState(visitData || null);
  const [savedVisitDataForNotConfirmed, setSavedVisitDataForNotConfirmed] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  const resolveCommitmentId = (obj) => {
    if (!obj) return null;
    if (obj.commitment_id_sf) return obj.commitment_id_sf;
    if (obj.commitmentId) return obj.commitmentId;
    if (obj.commitmentIdSf) return obj.commitmentIdSf;
    if (obj.id) return obj.id;
    if (obj.raw) {
      if (obj.raw.commitment_id_sf) return obj.raw.commitment_id_sf;
      if (obj.raw.commitmentId) return obj.raw.commitmentId;
      if (obj.raw.Id) return obj.raw.Id;
    }
    return null;
  };

  const resolveCommitmentPatchId = (obj) => {
    if (!obj) return null;
    const tryMatch = (item) => {
      if (!item) return false;
      if (obj.commitment_id_sf && (item.commitment_id_sf === obj.commitment_id_sf)) return true;
      if (obj.commitmentId && (item.commitmentId === obj.commitmentId)) return true;
      if (obj.id && (item.id === obj.id || item.Id === obj.id)) return true;
      if (item.Id && obj.raw && obj.raw.Id && item.Id === obj.raw.Id) return true;
      return false;
    };

    if (Array.isArray(obj.raw) && obj.raw.length > 0) {
      const found = obj.raw.find(tryMatch) || obj.raw[0];
      return found && (found.id || found.Id) ? (found.id || found.Id) : null;
    }

    if (obj.raw && (obj.raw.id || obj.raw.Id)) return obj.raw.id || obj.raw.Id;

     if (obj.id) return obj.id;
    if (obj.raw && obj.raw.commitment_id_sf && obj.raw.id) return obj.raw.id;
    return null;
  };


  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (visitData) {
      setLocalVisitData(visitData);
      setShowVisitModal(true);
    }
  }, [visitData]);

  const callOnClearVisit = () => {
    try {
      console.log('VisitModalController: calling onClearVisit()', { visitDataPresent: !!visitData, time: new Date().toISOString() });
    } catch (e) { }
    try { setLocalVisitData(null); } catch (e) {}
    onClearVisit && onClearVisit();
  };

  const handleVisitConfirm = async (data) => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) {
      console.warn('Nenhum commitmentId disponível para confirmar visita');
      setShowVisitModal(false);
      callOnClearVisit();
      return;
    }

    const payload = {
      user_id: (data && typeof data.user_id !== 'undefined') ? data.user_id : localStorage.getItem('userId'),
      confirm: (data && typeof data.confirm !== 'undefined') ? data.confirm : 'true',
    };

    setTimeout(async () => {
      try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_confirmed', { commitmentId }); } catch (e) {}
      } catch (err) {
        console.warn('Erro ao confirmar visita', err);
      } finally {
        if (isMountedRef.current) {
          setShowVisitModal(false);
          setShowVisitValidationModal(true);
        }
      }
    }, 0);
  };

  const handleVisitDeny = async (payload) => {
    setIsProcessing(true);
    setTimeout(async () => {
      try {
        const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
        if (!commitmentId) {
          console.warn('Nenhum commitmentId disponível para negar visita');
          setShowVisitModal(false);
          callOnClearVisit();
          return;
        }

         const payloadToSend = {
          user_id: (payload && typeof payload.user_id !== 'undefined') ? payload.user_id : localStorage.getItem('userId'),
          confirm: (payload && typeof payload.confirm !== 'undefined') ? payload.confirm : 'false',
          finished: (payload && typeof payload.finished !== 'undefined') ? payload.finished : 'true',
        };

        const forSetDenied = resolveCommitmentPatchId(localVisitData) || commitmentId;
        if (isMountedRef.current) {
          setShowVisitModal(false);
          setSavedVisitDataForNotConfirmed(localVisitData);
          setDeniedCommitmentId(forSetDenied);
        }
        await updateCommitment(commitmentId, payloadToSend);
        try { logEvent(analytics, 'visit_denied', { commitmentId }); } catch (e) {}
        if (isMountedRef.current) setShowVisitNotConfirmedModal(true);
      } catch (err) {
        console.warn('Erro ao negar visita', err);
      } finally {
        setIsProcessing(false);
      }
    }, 0);
  };

  const handleVisitClose = () => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    setTimeout(async () => {
      if (!commitmentId) {
        if (isMountedRef.current) {
          callOnClearVisit();
        }
        return;
      }

      const payloadToSend = {
        user_id: localStorage.getItem('userId'),
        close: true,
      };

      try {
        await updateCommitment(commitmentId, payloadToSend);
        try { logEvent(analytics, 'visit_closed', { commitmentId }); } catch (e) {}
      } catch (err) {
        console.warn('Erro ao fechar visita', err);
      } finally {
        if (isMountedRef.current) {
          setShowVisitModal(false);
          onClearVisit && onClearVisit();
        }
      }
    }, 0);
  };

  const handleNotConfirmedSubmit = async (comment) => {
    const commitmentId = deniedCommitmentId;
    if (!commitmentId) {
      setShowVisitNotConfirmedModal(false);
      return;
    }

    const payloadToSend = {
      user_id: localStorage.getItem('userId'),
      comment: comment || '',
    };

    try {
      await updateCommitment(commitmentId, payloadToSend);
      if (isMountedRef.current) {
        setShowVisitNotConfirmedModal(false);
        setDeniedCommitmentId(null);
        setShowResponseRegisteredModal(true);
      }
    } catch (err) {
      console.warn('Erro ao enviar comentário de não confirmado', err);
      if (isMountedRef.current) {
        setShowVisitNotConfirmedModal(false);
        setDeniedCommitmentId(null);
        callOnClearVisit();
      }
    }
  };

  const handleValidationImmediate = async () => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) {
      console.warn('Nenhum commitmentId disponível para confirmação imediata');
      return;
    }

    const payload = {
      user_id: localStorage.getItem('userId'),
      accordance: true,
      finished: true,
    };

    setTimeout(async () => {
      try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_validation_immediate', { commitmentId }); } catch (e) {}
        if (isMountedRef.current) {
          setFeedbackType('agreement');
          setShowFeedbackModal(true);
        }
      } catch (err) {
        console.warn('Erro ao enviar confirmação imediata', err);
      } finally {

      }
    }, 0);
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) return;

    const userIdToSend = localStorage.getItem('userId') || localStorage.getItem('ownerid');
    const payloadToSend = { user_id: userIdToSend };

    if (feedbackData) {
      if (typeof feedbackData.selected_items !== 'undefined') {
        if (Array.isArray(feedbackData.selected_items)) {
          payloadToSend.selected_items = feedbackData.selected_items.join(', ');
        } else {
          payloadToSend.selected_items = String(feedbackData.selected_items || '').trim();
        }

        if (feedbackData.comment && typeof feedbackData.comment === 'string' && feedbackData.comment.trim()) {
          payloadToSend.comment = feedbackData.comment.trim();
        }
      } else {
        if (feedbackData.positive || feedbackData.negative) {
          const pos = Array.isArray(feedbackData.positive) ? feedbackData.positive.join('; ') : '';
          const neg = Array.isArray(feedbackData.negative) ? feedbackData.negative.join('; ') : '';
          payloadToSend.comment = `Positivos: ${pos}` + (neg ? ` | Negativos: ${neg}` : '') + (feedbackData.comments ? ` | Observação: ${feedbackData.comments}` : '');
        } else if (feedbackData.divergences) {
          const divs = Array.isArray(feedbackData.divergences) ? feedbackData.divergences.join('; ') : '';
          payloadToSend.comment = `Divergências: ${divs}` + (feedbackData.comments ? ` | Observação: ${feedbackData.comments}` : '');
        }
      }
    }

    setTimeout(async () => {
      try {
        await updateCommitment(commitmentId, payloadToSend);
        try { logEvent(analytics, 'visit_feedback_submitted', { commitmentId }); } catch (e) {}
      } catch (err) {
        try {
          const status = err && err.response && err.response.status;
          if (status === 400 && Array.isArray(payloadToSend.selected_items)) {
            const itemsStr = payloadToSend.selected_items.join('; ');
            const fallbackComment = itemsStr + (payloadToSend.comment ? ` | Observação: ${payloadToSend.comment}` : '');
            const fallbackPayload = { user_id: payloadToSend.user_id, comment: fallbackComment };
            console.debug('updateCommitment: retrying with fallback comment payload due to 400', { fallbackPayload });
            await updateCommitment(commitmentId, fallbackPayload);
            try { logEvent(analytics, 'visit_feedback_submitted_fallback', { commitmentId }); } catch (e) {}
            return;
          }
        } catch (e) {
          console.warn('Fallback retry failed', e);
        }

        console.warn('Erro ao enviar feedback da visita', err);
      }
    }, 0);
  };

  const handlePatchRating = async (ratingData) => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) return;

    const payload = {
      user_id: ratingData.user_id || localStorage.getItem('userId'),
      visit_rating: ratingData.visit_rating,
    };

    if (ratingData.hasOwnProperty('comment_rating')) {
      payload.comment_rating = ratingData.comment_rating;
    }

    setTimeout(async () => {
      try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_rating_submitted', { commitmentId }); } catch (e) {}
      } catch (err) {
        console.warn('Erro ao enviar rating da visita', err);
      }
    }, 0);
  };

  const handleValidationImmediateDeny = async () => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) {
      console.warn('Nenhum commitmentId disponível para negar imediatamente');
      return;
    }

    const payload = {
      user_id: localStorage.getItem('userId'),
      accordance: false,
      finished: true,
    };

    setTimeout(async () => {
      try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_validation_immediate_denied', { commitmentId }); } catch (e) {}
        if (isMountedRef.current) {
          setFeedbackType('disagreement');
          setShowFeedbackModal(true);
        }
      } catch (err) {
        console.warn('Erro ao enviar negação imediata', err);
      } finally {
        // no loader changes
      }
    }, 0);
  };

  const handleValidationConfirm = async (data) => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) {
      console.warn('Nenhum commitmentId disponível para validar visita');
      setShowVisitValidationModal(false);
      callOnClearVisit();
      return;
    }

    if (data && data.patched) {
      return;
    }

    const payload = {
      user_id: localStorage.getItem('userId'),
      accordance: true,
      finished: true,
      comment: data && data.comment ? data.comment : '',
      visit_rating: data && typeof data.visit_rating !== 'undefined' ? data.visit_rating : 0,
    };

    setTimeout(async () => {
        try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_validation_confirmed', { commitmentId }); } catch (e) {}
        if (isMountedRef.current) {
          setShowVisitValidationModal(false);
          callOnClearVisit();
        }
      } catch (err) {
        console.warn('Erro ao confirmar validação', err);
      } finally {
        // no loader changes
      }
    }, 0);
  };

  const handleValidationClose = () => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    setShowVisitValidationModal(false);
    setShowVisitModal(false);

    setTimeout(async () => {
      if (!commitmentId) {
        if (isMountedRef.current) {
          callOnClearVisit();
        }
        return;
      }

      const payloadToSend = {
        user_id: localStorage.getItem('userId'),
        close: true,
      };

      try {
        await updateCommitment(commitmentId, payloadToSend);
        try { logEvent(analytics, 'visit_validation_closed', { commitmentId }); } catch (e) {}
      } catch (err) {
        console.warn('Erro ao fechar validação da visita', err);
      } finally {
        if (isMountedRef.current) {
          callOnClearVisit();
        }
      }
    }, 0);
  };

  const handleValidationDeny = async (data) => {
    const commitmentId = resolveCommitmentPatchId(localVisitData) || resolveCommitmentId(localVisitData);
    if (!commitmentId) {
      console.warn('Nenhum commitmentId disponível para negar validação');
      setShowVisitValidationModal(false);
      callOnClearVisit();
      return;
    }

    if (data && data.patched) {
      return;
    }

    const payload = {
      user_id: localStorage.getItem('userId'),
      accordance: false,
      finished: true,
      comment: data && data.comment ? data.comment : '',
      visit_rating: data && typeof data.visit_rating !== 'undefined' ? data.visit_rating : 0,
    };

    setTimeout(async () => {
        try {
        await updateCommitment(commitmentId, payload);
        try { logEvent(analytics, 'visit_validation_denied', { commitmentId }); } catch (e) {}
        if (isMountedRef.current) {
          setShowVisitValidationModal(false);
          callOnClearVisit();
        }
      } catch (err) {
        console.warn('Erro ao negar validação', err);
      } finally {
        // no loader changes
      }
    }, 0);
  };

  const handleThankYouNext = async (feedbackData) => {
    if (feedbackData) {
      await handleFeedbackSubmit(feedbackData);
    }
    setShowFeedbackModal(false);
    setShowConsultantRatingModal(true);
  };

  const handleThankYouBack = () => {
    setShowFeedbackModal(false);
    setShowVisitValidationModal(true);
  };

  const handleThankYouClose = async () => {
    setShowFeedbackModal(false);
    setShowVisitValidationModal(false);
    setShowResponseRegisteredModal(false);
    setShowConsultantRatingModal(false);
    setLocalVisitData(null);
    callOnClearVisit();
  };

  const handleConsultantRatingNext = async (ratingData) => {
    if (ratingData && ratingData.patched) {
      setShowConsultantRatingModal(false);
      setShowVisitValidationModal(false);
      setShowResponseRegisteredModal(true);
    }
  };

  const handleConsultantRatingBack = () => {
    setShowConsultantRatingModal(false);
    setShowFeedbackModal(true);
  };

  const handleConsultantRatingClose = () => {
    setShowConsultantRatingModal(false);
    setShowFeedbackModal(false);
    setShowVisitValidationModal(false);
    setShowResponseRegisteredModal(false);
    setLocalVisitData(null);
    callOnClearVisit();
  };

  return (
    <>
      {showVisitModal && localVisitData && (
        <VisitConfirmationModal
          isOpen={showVisitModal}
          onClose={handleVisitClose}
          consultantName={localVisitData.consultantName}
          visitDate={localVisitData.commitmentDate}
          visitTime={localVisitData.commitmentDate}
          onImmediateConfirm={handleVisitConfirm}
          onConfirmFinal={handleValidationConfirm}
          onDeny={handleVisitDeny}
        />
      )}

      {showVisitValidationModal && localVisitData && (
        <VisitValidationModal
          isOpen={showVisitValidationModal}
          onClose={handleValidationClose}
          onCloseWithoutApi={() => { setShowVisitValidationModal(false); }}
          onBack={() => { setShowVisitValidationModal(false); setShowVisitModal(true); }}
          onPatchRating={handlePatchRating}
          consultantName={localVisitData.consultantName}
          visitDate={localVisitData.commitmentDate}
          visitTime={localVisitData.commitmentDate}
          appointmentId={localVisitData.commitment_id_sf || localVisitData.commitmentId || localVisitData.commitmentIdSf}
          onConfirm={handleValidationConfirm}
          onDeny={handleValidationDeny}
          onImmediateConfirm={handleValidationImmediate}
          onImmediateDeny={handleValidationImmediateDeny}
          onFeedbackSubmit={handleFeedbackSubmit}
          showFeedbackModal={showFeedbackModal}
          setShowFeedbackModal={setShowFeedbackModal}
          feedbackType={feedbackType}
          setFeedbackType={setFeedbackType}
          showConsultantRatingModal={showConsultantRatingModal}
          setShowConsultantRatingModal={setShowConsultantRatingModal}
        />
      )}

      {showVisitNotConfirmedModal && (
        <VisitNotConfirmedModal
          isOpen={showVisitNotConfirmedModal}
          onClose={() => { setShowVisitNotConfirmedModal(false); setDeniedCommitmentId(null); callOnClearVisit(); }}
          onBack={() => { setShowVisitNotConfirmedModal(false); setLocalVisitData(savedVisitDataForNotConfirmed); setShowVisitModal(true); }}
          onSubmit={handleNotConfirmedSubmit}
          onPatchRating={handlePatchRating}
        />
      )}

      {showResponseRegisteredModal && (
        <ResponseRegisteredModal
          isOpen={showResponseRegisteredModal}
          onClose={() => {
            setShowResponseRegisteredModal(false);
            callOnClearVisit();
          }}
        />
      )}

      <FeedbackModal
        type={feedbackType}
        isOpen={showFeedbackModal}
        onClose={handleThankYouClose}
        onBack={handleThankYouBack}
        onNext={handleThankYouNext}
      />

      <ConsultantRatingModal
        isOpen={showConsultantRatingModal}
        onClose={handleConsultantRatingClose}
        onBack={handleConsultantRatingBack}
        onNext={handleConsultantRatingNext}
        onPatchRating={handlePatchRating}
      />

      {isProcessing && (
        <div aria-hidden style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <Loading />
        </div>
      )}
    </>
  );
};

export default VisitModalController;
